import { Field, inputCls, Select } from "./common";
import { EN } from "../lib/i18n";

const T = (t, k, fb) => (t ? t(k) : EN[k] || fb || k);

export const PARISH_CITY = "General Santos City";
export const PARISH_BARANGAYS = ["Barangay Mabuhay", "Barangay San Isidro", "Barangay Conel"];
const BAD_SUFFIX = ["na", "n/a", "n.a", "n.a.", "none", "-", "--", "nil", "null", "wala"];
export const badSuffix = (s) => BAD_SUFFIX.includes((s || "").trim().toLowerCase().replace(/\s/g, ""));

export const emptyParticipant = (type = "parish") => ({
  type, first_name: "", middle_name: "", last_name: "", suffix: "", full_name: "", zone_id: null, gkk_id: null, unit_id: null,
  affiliations: [], address: "", barangay: "", city: type === "parish" ? PARISH_CITY : "", province: "", mobile: "", email: "", facebook: "", is_minor: false,
  title: "", organization: "", diocese: "", designation: "",
});

const Txt = ({ label, v, k, set, required, testid, type = "text", inputMode }) => (
  <Field label={label} required={required}>
    <input className={inputCls} value={v[k] || ""} type={type} inputMode={inputMode} data-testid={testid}
      onChange={(e) => set({ ...v, [k]: e.target.value })} autoComplete="off" />
  </Field>
);

function ParishFields({ v, set, dir, t, p }) {
  const gkks = dir.gkks.filter((g) => g.zone_id === v.zone_id);
  const gkk = dir.gkks.find((g) => g.id === v.gkk_id);
  const units = dir.units.filter((u) => u.gkk_id === v.gkk_id);
  return (
    <>
      <Section title={T(t, "personal", "Name")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Txt label={T(t, "firstName")} v={v} k="first_name" set={set} required testid={`${p}-first-name`} />
          <Txt label={T(t, "middleName")} v={v} k="middle_name" set={set} testid={`${p}-middle-name`} />
          <Txt label={T(t, "lastName")} v={v} k="last_name" set={set} required testid={`${p}-last-name`} />
          <div>
            <Txt label={T(t, "suffix")} v={v} k="suffix" set={set} testid={`${p}-suffix`} />
            {badSuffix(v.suffix) && <span className="block text-sm text-red-700 mt-1" data-testid={`${p}-suffix-error`}>{T(t, "suffixNA", "Do not write N/A. Leave blank if you have no suffix.")}</span>}
          </div>
        </div>
      </Section>
      <Section title={T(t, "parishInfo", "GKK")}>
        <div className="grid gap-4">
          <Field label={T(t, "zone")} required>
            <Select testid={`${p}-zone`} value={v.zone_id} placeholder={T(t, "selectZone")}
              options={dir.zones.map((z) => ({ value: z.id, label: z.name }))}
              onChange={(z) => set({ ...v, zone_id: z, gkk_id: null, unit_id: null })} />
          </Field>
          <Field label={T(t, "gkk")} required>
            <Select testid={`${p}-gkk`} value={v.gkk_id} disabled={!v.zone_id}
              placeholder={v.zone_id ? T(t, "selectGkk") : T(t, "selectZoneFirst")}
              options={gkks.map((g) => ({ value: g.id, label: g.locality ? `${g.name} – ${g.locality}` : g.name }))}
              onChange={(g) => set({ ...v, gkk_id: g, unit_id: null })} />
          </Field>
          <div className="min-h-[52px] rounded-xl bg-[#F5F0E6] border border-[#E5DEC9] px-4 py-3 text-sm text-stone-700" data-testid={`${p}-gkk-address`}>
            <span className="font-semibold text-stone-500 text-xs uppercase tracking-wider block">{T(t, "address")}</span>
            {gkk ? `${gkk.address} · ${gkk.id}` : "—"}
          </div>
          <Field label={T(t, "unit")}>
            <Select testid={`${p}-unit`} value={v.unit_id} placeholder={T(t, "selectUnit")} disabled={!v.gkk_id || !units.length}
              options={units.map((u) => ({ value: u.id, label: u.name }))} onChange={(u) => set({ ...v, unit_id: u })} />
          </Field>
        </div>
      </Section>
      <Section title={T(t, "contact")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><Txt label={T(t, "resAddress")} v={v} k="address" set={set} testid={`${p}-address`} /></div>
          <Field label={T(t, "barangay")} required>
            <Select testid={`${p}-barangay`} value={v.barangay} placeholder={T(t, "selectBarangay", "Select Barangay")}
              options={PARISH_BARANGAYS.map((b) => ({ value: b, label: b }))} onChange={(b) => set({ ...v, barangay: b || "", city: PARISH_CITY })} />
          </Field>
          <Field label={T(t, "city")}>
            <input className={inputCls} value={PARISH_CITY} readOnly disabled data-testid={`${p}-city`} />
          </Field>
          <Txt label={T(t, "mobile")} v={v} k="mobile" set={set} testid={`${p}-mobile`} type="tel" inputMode="tel" />
          <Txt label={T(t, "email")} v={v} k="email" set={set} testid={`${p}-email`} type="email" />
          <div className="sm:col-span-2"><Txt label={T(t, "facebook")} v={v} k="facebook" set={set} testid={`${p}-facebook`} /></div>
        </div>
        <label className="flex items-center gap-3 mt-4 text-sm text-stone-700 min-h-[44px]">
          <input type="checkbox" className="h-5 w-5 accent-[#2D3B2D]" checked={!!v.is_minor} data-testid={`${p}-is-minor`}
            onChange={(e) => set({ ...v, is_minor: e.target.checked })} />
          {T(t, "minor")}
        </label>
      </Section>
    </>
  );
}

function ExternalFields({ v, set, t, p }) {
  return (
    <Section title={T(t, "personal", "Name")}>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><Txt label={T(t, "fullName")} v={v} k="full_name" set={set} required testid={`${p}-full-name`} /></div>
        <Txt label={T(t, "title")} v={v} k="title" set={set} testid={`${p}-title`} />
        <Txt label={T(t, "designation")} v={v} k="designation" set={set} testid={`${p}-designation`} />
        <div className="sm:col-span-2"><Txt label={T(t, "org")} v={v} k="organization" set={set} testid={`${p}-organization`} /></div>
        <div className="sm:col-span-2"><Txt label={T(t, "diocese")} v={v} k="diocese" set={set} testid={`${p}-diocese`} /></div>
        <Txt label={T(t, "province")} v={v} k="province" set={set} testid={`${p}-province`} />
        <Txt label={T(t, "city")} v={v} k="city" set={set} testid={`${p}-city`} />
        <Txt label={T(t, "barangay")} v={v} k="barangay" set={set} testid={`${p}-barangay`} />
        <Txt label={T(t, "resAddress")} v={v} k="address" set={set} testid={`${p}-address`} />
        <Txt label={T(t, "mobile")} v={v} k="mobile" set={set} testid={`${p}-mobile`} type="tel" inputMode="tel" />
        <Txt label={T(t, "email")} v={v} k="email" set={set} testid={`${p}-email`} type="email" />
        <div className="sm:col-span-2"><Txt label={T(t, "facebook")} v={v} k="facebook" set={set} testid={`${p}-facebook`} /></div>
      </div>
    </Section>
  );
}

export const Section = ({ title, children }) => (
  <fieldset className="sfap-card p-5 sm:p-6">
    <legend className="sr-only">{title}</legend>
    <h3 className="font-serif text-lg font-bold text-[#2D3B2D] mb-4">{title}</h3>
    {children}
  </fieldset>
);

export function ParticipantFields({ value, onChange, dir, t, prefix = "pf" }) {
  return value.type === "external"
    ? <ExternalFields v={value} set={onChange} t={t} p={prefix} />
    : <ParishFields v={value} set={onChange} dir={dir} t={t} p={prefix} />;
}

export function RepresentFields({ value, onChange, dir, t, type, prefix = "rep" }) {
  const set = (k, x) => onChange({ ...value, [k]: x });
  const resource = ["Invited Resource Speaker", "Guest Presider", "Facilitator", "Panelist"].includes(value.role_in_meeting);
  return (
    <Section title={T(t, "represent")}>
      <div className="grid gap-4">
        {type === "parish" ? (
          <>
            <Field label={T(t, "ministry")}>
              <Select testid={`${prefix}-ministry`} value={value.ministry_id} placeholder="—"
                options={dir.ministries.map((m) => ({ value: m.id, label: m.name }))} onChange={(x) => set("ministry_id", x)} />
            </Field>
            <Field label={T(t, "position")} required>
              <Select testid={`${prefix}-position`} value={value.position} placeholder="—"
                options={dir.positions.map((x) => ({ value: x, label: x }))} onChange={(x) => set("position", x)} />
            </Field>
            {value.position === "Other" && (
              <input className={inputCls} placeholder="Specify position" value={value.position_other || ""} data-testid={`${prefix}-position-other`}
                onChange={(e) => set("position_other", e.target.value)} />
            )}
          </>
        ) : (
          <>
            <Field label={T(t, "role")} required>
              <Select testid={`${prefix}-role`} value={value.role_in_meeting} placeholder="—"
                options={dir.meeting_roles.map((x) => ({ value: x, label: x }))} onChange={(x) => set("role_in_meeting", x)} />
            </Field>
            {resource && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field label={T(t, "topic")}><input className={inputCls} value={value.topic || ""} data-testid={`${prefix}-topic`} onChange={(e) => set("topic", e.target.value)} /></Field>
                </div>
                <Field label={T(t, "invitingMinistry")}><input className={inputCls} value={value.inviting_ministry || ""} data-testid={`${prefix}-inviting-ministry`} onChange={(e) => set("inviting_ministry", e.target.value)} /></Field>
                <Field label={T(t, "invitingOfficer")}><input className={inputCls} value={value.inviting_officer || ""} data-testid={`${prefix}-inviting-officer`} onChange={(e) => set("inviting_officer", e.target.value)} /></Field>
              </div>
            )}
          </>
        )}
      </div>
    </Section>
  );
}

export function validParticipant(v) {
  if (v.type === "external") return !!v.full_name?.trim();
  return !!(v.first_name?.trim() && v.last_name?.trim() && v.zone_id && v.gkk_id && PARISH_BARANGAYS.includes(v.barangay) && !badSuffix(v.suffix));
}

export const withAffiliation = (v, rep) =>
  v.type === "parish" && rep.ministry_id && !v.affiliations?.some((a) => a.ministry_id === rep.ministry_id)
    ? { ...v, affiliations: [...(v.affiliations || []), { ministry_id: rep.ministry_id, position: rep.position, position_other: rep.position_other }] }
    : v;
